import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import {
  Area,
  AreaRevealShape,
  type AreaRevealShapeProps,
  CartesianGrid,
  LabelList,
  ComposedChart as RechartsLineChart,
  useActiveTooltipDataPoints,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CHART_PALETTE,
  type ChartConfig,
  ChartTooltip,
  ChartTooltipContent,
  useChartKeyColors,
} from '@/components/ui/chart';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { useSeriesHighlight } from '@/hooks/use-series-highlight';
import { cn } from '@/lib/utils';
import type { ChartDataPoint, ChartSeries } from '@/types/data';
import { CartesianChartFrame } from '../cartesian-chart-frame';
import {
  estimateChartAxisWidth,
  getFinitePositiveChartValue,
  sanitizeChartKey,
  toSeriesChartData,
} from '../chart-data';
import { ChartLegend as WrapperChartLegend } from '../chart-legend';

// Default chart colors — shared categorical palette (mode-aware via tokens).
const DEFAULT_CHART_COLORS = [...CHART_PALETTE];

const lineChartVariants = cva('relative w-full', {
  variants: {
    size: {
      sm: 'h-40',
      default: 'h-60',
      lg: 'h-80',
      xl: 'h-96',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

export type { ChartDataPoint, ChartSeries };

export type LegendPosition = 'none' | 'top' | 'right';

export interface LineChartProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof lineChartVariants> {
  /** Data points for the chart */
  data: ChartDataPoint[];
  /** Series configuration */
  series: ChartSeries[];
  /** Show Y-axis labels and grid lines */
  showYAxis?: boolean;
  /** Show X-axis labels */
  showXAxis?: boolean;
  /** Show horizontal grid lines */
  showGrid?: boolean;
  /** Recharts interval strategy for X-axis tick labels */
  xAxisInterval?:
    | number
    | 'preserveStart'
    | 'preserveEnd'
    | 'preserveStartEnd'
    | 'equidistantPreserveStart';
  /** Y-axis label (title) */
  yAxisLabel?: string;
  /** X-axis label (title) */
  xAxisLabel?: string;
  /** Legend position */
  legendPosition?: LegendPosition;
  /** Custom Y-axis tick values */
  yAxisTicks?: number[];
  /** Format function for Y-axis values */
  yAxisFormat?: (value: number) => string;
  /** Show area fill under lines */
  showArea?: boolean;
  /** Stroke width for lines */
  strokeWidth?: number;
  /** Show dots on data points */
  showDots?: boolean;
  /** Data point marker treatment */
  dotStyle?: 'solid' | 'ring' | 'palette';
  /** Show labels at each data point */
  showValueLabels?: boolean;
  /** Use the numeric value or category label for data labels */
  valueLabelType?: 'value' | 'label';
  /** Format numeric data labels */
  valueLabelFormatter?: (value: number) => string | number;
  /** Curve type for lines */
  curveType?: 'linear' | 'smooth' | 'step';
  /** Show tooltip on hover */
  showTooltip?: boolean;
  /** Accessible label for screen readers */
  'aria-label'?: string;
}

const sanitizeKey = sanitizeChartKey;

interface ActiveDotProps {
  cx?: number;
  cy?: number;
  stroke?: string;
  fill?: string;
  className?: string;
}

/** Custom active dot with subtle ring/halo effect */
function ActiveDot({
  cx = 0,
  cy = 0,
  stroke,
  fill,
  className,
}: ActiveDotProps) {
  const color = stroke || fill || 'currentColor';
  return (
    <g className={className}>
      <circle cx={cx} cy={cy} r={10} fill={color} fillOpacity={0.15} />
      <circle
        cx={cx}
        cy={cy}
        r={5}
        fill={color}
        stroke="var(--background)"
        strokeWidth={2}
      />
    </g>
  );
}

/** One renderer timeline owns fill, outline and dots. Never replay a reveal. */
function ContourShape({
  data,
  ...props
}: AreaRevealShapeProps & { data: Record<string, unknown>[] }) {
  const activeData = useActiveTooltipDataPoints();
  const index = data.findIndex((point) => activeData?.includes(point));
  const point = props.points?.[index];
  return (
    <>
      <AreaRevealShape {...props} isEntrance={false} />
      {point?.x != null && point.y != null && (
        <ActiveDot
          cx={point.x}
          cy={point.y}
          stroke={props.stroke}
          className="recharts-active-dot"
        />
      )}
    </>
  );
}

function LineChart({
  data,
  series,
  showYAxis = true,
  showXAxis = true,
  showGrid = true,
  xAxisInterval,
  yAxisLabel,
  xAxisLabel,
  legendPosition = 'none',
  yAxisTicks,
  yAxisFormat = (v) => v.toLocaleString(),
  showArea = true,
  strokeWidth = 2,
  showDots = false,
  dotStyle = 'solid',
  showValueLabels = false,
  valueLabelType = 'value',
  valueLabelFormatter = (value) => value.toLocaleString(),
  curveType = 'smooth',
  showTooltip = true,
  size,
  className,
  'aria-label': ariaLabel,
  ...props
}: LineChartProps) {
  const prefersReducedMotion = useReducedMotion();
  const instanceId = React.useId().replace(/:/g, '');
  const { highlightedSeries, setHighlightedSeries, getOpacity } =
    useSeriesHighlight(prefersReducedMotion);

  const chartData = React.useMemo(
    () => toSeriesChartData(data, series),
    [data, series],
  );
  // Point identity/order changes have no safe positional correspondence.
  // Reset only marks at topology/preference boundaries, never the frame.
  const topology = JSON.stringify([
    data.map((point) => [point.id, point.label]),
    curveType,
    showArea,
    prefersReducedMotion,
  ]);
  const [motionInput, setMotionInput] = React.useState({
    data,
    topology,
    animate: false,
  });
  if (motionInput.data !== data || motionInput.topology !== topology) {
    setMotionInput({
      data,
      topology,
      animate: motionInput.topology === topology && !prefersReducedMotion,
    });
  }
  const finiteStrokeWidth = getFinitePositiveChartValue(strokeWidth, 2);
  const yAxisWidth = React.useMemo(
    () =>
      estimateChartAxisWidth({
        data,
        series,
        ticks: yAxisTicks,
        formatter: yAxisFormat,
        minWidth: 48,
        maxWidth: 112,
      }),
    [data, series, yAxisFormat, yAxisTicks],
  );

  const keyColors = useChartKeyColors(
    series.map((item, index) => item.id ?? index),
  );
  const pointColors = useChartKeyColors(
    data.map((item, index) => item.id ?? index),
  );
  const chartConfig = React.useMemo(() => {
    const config: ChartConfig = {};
    for (const [i, s] of series.entries()) {
      const key = sanitizeKey(s.id);
      config[key] = {
        label: s.label,
        color: s.color || keyColors.get(s.id ?? i),
      };
    }
    return config;
  }, [series, keyColors]);

  const rechartsCurveType =
    curveType === 'smooth'
      ? 'monotone'
      : curveType === 'step'
        ? 'step'
        : 'linear';

  return (
    <div
      data-slot="line-chart"
      className={cn(lineChartVariants({ size }), className)}
      {...props}
    >
      <CartesianChartFrame
        ariaLabel={ariaLabel || 'Line chart'}
        ariaLabelledBy={props['aria-labelledby']}
        config={chartConfig}
        series={series.map((item, index) => ({
          ...item,
          color: item.color || keyColors.get(item.id ?? index),
        }))}
        legendPosition={legendPosition}
        highlightedSeries={highlightedSeries}
        onSeriesHover={setHighlightedSeries}
        yAxisLabel={yAxisLabel}
        topLegendAlign="end"
      >
        <RechartsLineChart
          data={chartData}
          margin={{
            top: 8,
            right: 12,
            bottom: xAxisLabel ? 20 : 0,
            left: 12,
          }}
        >
          {showGrid && (
            <CartesianGrid
              strokeDasharray="0"
              vertical={false}
              className="stroke-border"
            />
          )}
          {showXAxis && (
            <XAxis
              dataKey="label"
              interval={xAxisInterval}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
              label={
                xAxisLabel
                  ? {
                      value: xAxisLabel,
                      position: 'bottom',
                      offset: 0,
                      style: {
                        fill: 'var(--muted-foreground)',
                        fontSize: 12,
                      },
                    }
                  : undefined
              }
            />
          )}
          {showYAxis && (
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tick={{
                fill: 'var(--muted-foreground)',
                fontSize: 12,
                direction: 'ltr',
              }}
              tickFormatter={yAxisFormat}
              ticks={yAxisTicks}
              width={yAxisWidth}
            />
          )}
          {showTooltip && (
            <ChartTooltip
              content={<ChartTooltipContent />}
              cursor={
                prefersReducedMotion ? false : { stroke: 'var(--border)' }
              }
            />
          )}
          {/* Area fills (rendered first, behind lines) */}
          {showArea &&
            series.map((s) => {
              const key = sanitizeKey(s.id);
              const gradientId = `fill-${instanceId}-${key}`;
              return (
                <defs key={`gradient-${key}`}>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor={`var(--color-${key})`}
                      stopOpacity={0.2}
                    />
                    <stop
                      offset="100%"
                      stopColor={`var(--color-${key})`}
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                </defs>
              );
            })}
          {/* A filled series uses Area's shared outline/fill/dot geometry. */}
          {series.map((s) => {
            const key = sanitizeKey(s.id);
            const opacityClass = cn(
              'transition-opacity duration-(--duration-moderate) ease-standard motion-reduce:transition-none',
              getOpacity(s.id) === 0.2 ? 'opacity-20' : 'opacity-100',
            );
            const renderDot = showDots
              ? (dotProps: {
                  cx?: number;
                  cy?: number;
                  index?: number;
                  className?: string;
                }) => {
                  const cx = dotProps.cx ?? 0;
                  const cy = dotProps.cy ?? 0;
                  const pointColor =
                    dotStyle === 'palette'
                      ? s.color ||
                        pointColors.get(
                          data[dotProps.index ?? 0]?.id ?? dotProps.index ?? 0,
                        )
                      : `var(--color-${key})`;
                  return (
                    <circle
                      className={dotProps.className}
                      cx={cx}
                      cy={cy}
                      r={dotStyle === 'ring' ? 4 : 3.5}
                      fill={
                        dotStyle === 'ring' ? 'var(--background)' : pointColor
                      }
                      stroke={pointColor}
                      strokeWidth={dotStyle === 'ring' ? 1.5 : 0}
                    />
                  );
                }
              : false;
            return (
              <Area
                key={`${topology}-${key}`}
                id={`line-${instanceId}-${key}`}
                dataKey={key}
                type={rechartsCurveType}
                stroke={`var(--color-${key})`}
                strokeWidth={finiteStrokeWidth}
                fill={showArea ? `url(#fill-${instanceId}-${key})` : 'none'}
                shape={(shapeProps: AreaRevealShapeProps) => (
                  <>
                    <ContourShape {...shapeProps} data={chartData} />
                    {typeof renderDot === 'function' &&
                      shapeProps.points?.map((point, index) => (
                        <React.Fragment key={point.x}>
                          {renderDot({
                            cx: point.x ?? undefined,
                            cy: point.y ?? undefined,
                            index,
                          })}
                        </React.Fragment>
                      ))}
                  </>
                )}
                // Recharts portals ordinary dots into a separate z-index layer.
                // Keep multi-point dots in the contour so emphasis is inherited.
                dot={data.length === 1 ? renderDot : false}
                // Recharts skips custom area shapes for a single point.
                activeDot={data.length === 1 ? <ActiveDot /> : false}
                className={opacityClass}
                isAnimationActive={motionInput.animate && !prefersReducedMotion}
                animationBegin={0}
                animationDuration={200}
                animationEasing="ease-out"
              >
                {showValueLabels && (
                  <LabelList
                    dataKey={valueLabelType === 'label' ? 'label' : key}
                    position="top"
                    offset={8}
                    className="fill-foreground text-[10px]"
                    formatter={
                      valueLabelType === 'value'
                        ? (value) => valueLabelFormatter(Number(value))
                        : undefined
                    }
                  />
                )}
              </Area>
            );
          })}
        </RechartsLineChart>
      </CartesianChartFrame>
    </div>
  );
}

LineChart.displayName = 'LineChart';

export {
  DEFAULT_CHART_COLORS,
  LineChart,
  lineChartVariants,
  sanitizeKey,
  WrapperChartLegend as ChartLegend,
};
